// Aide et support (D-082) :
//   #/aide               les tutos de mes casquettes (« Tout voir » : tous), recherche, par thème
//   #/aide/<tuto>        un tuto : étapes, « Y aller », bon à savoir
//   #/aide/ecrire        écrire à l'équipe Arceau : bug, idée, question ; capture ; contexte joint
//   #/aide/messages      mes messages, leur état et la réponse
//   #/aide/premiers-pas  mes premiers pas, tous mes rôles (D-091)
//   #/aide/donnees       les mentions légales et données du club (D-103), avec la politique de confidentialité
// Un compte mineur (D-103) lit les tutos qui le concernent, au texte qui lui parle (forAccount).
// Les tutos sont des données (src/help/articles.ts) ; la recherche est testée (src/help/help.test.ts).
// Guide interactif (D-091, maquettes c3, c4) : « Montre-moi » lance la visite du tuto dans l'app (même
// nom, src/guide/tours.ts), l'animation filmée sur la démo (public/guide/<tuto>.webp) le montre ;
// « Ce tuto t'a aidé ? » : « Oui » s'enregistre, « Pas vraiment » part au support (question, tuto:<slug>).
import { ARTICLES, forAccount, forModules, ROLE_LABEL, THEMES, type HelpArticle, type HelpRole } from '../help/articles';
import { PRIVACY_URL } from '../domain/legal';
import { clubLegal } from '../lib/minors-legal';
import { clubDataBody } from './club-data';
import { hostClubSlug } from './legal-links';
import { byTheme, forRoles, searchArticles, stepParts } from '../help/search';
import { BODY_MAX, DAILY_MAX, KIND_HINT, KIND_LABEL, SHOT_MAX, STATUS_CHIP, STATUS_LABEL, browserLabel, messageLabel, pageName, pageOf, type SupportKind } from '../help/support';
import { appVersion, isPlatformAdmin, mySupportMessages, sendSupport, type SupportMessage } from '../lib/support';
import { agree, type TeamGender } from '../domain/labels';
import { isMinorAccount, me, modOn, mvOpen, personaOf, siteOn, type Ctx, type Persona } from './context';
import { $, $$, html, mount, toast, type Html } from './dom';
import { TOURS } from '../guide/tours';
import { FILMS } from '../guide/films';
import { renderAllFirstSteps, renderPathCard } from '../guide/first-steps';
import { guideFeedback } from '../lib/guide';
import { tv as vousTv, vouvoie } from './vous';
import { notifRole } from './notifs';

// La page d'où l'on vient (jointe au message) : la dernière hors de l'aide.
let fromPage = '';
window.addEventListener('hashchange', (e) => {
  let old = '';
  try { old = new URL(e.oldURL).hash; } catch { /* adresse illisible */ }
  if (!old.startsWith('#/aide')) fromPage = old || '#/';
});

const SHOW_ALL = 'arceau.aide.tout';
const remembered = (): boolean => { try { return sessionStorage.getItem(SHOW_ALL) === '1'; } catch { return false; } };
const remember = (on: boolean) => { try { sessionStorage.setItem(SHOW_ALL, on ? '1' : ''); } catch { /* navigation privée */ } };

/** Mes rôles pour l'aide : toutes mes casquettes (pas seulement celle affichée). */
export function helpRoles(c: Ctx): Set<HelpRole> {
  const f = c.full, r = new Set<HelpRole>(['tous']);
  if (personaOf(c) === 'abonne') return r;   // l'abonné (D-110) : rien de la vie interne du club
  if (f.adminOf.length) r.add('bureau');
  if (f.coachOf.size) r.add('coach');
  const self = f.people.find(p => p.user_id === c.uid);
  if (self && f.memberships.some(m => m.person_id === self.id)) r.add('joueur');
  if (f.people.some(p => p.user_id !== c.uid)) r.add('parent');
  const partnerOnly = personaOf(c) === 'partenaire';
  if (c.partners.length) r.add('partenaire');
  // Tout compte adulte du club est bénévole (D-064) ; un partenaire seulement s'il est aussi supporter (D-071).
  const helper = c.roles.some(x => x.role === 'supporter');
  if ((partnerOnly ? helper : c.clubs.length > 0) && !isMinorAccount(c)) r.add('benevole');
  return r;
}

/** Genre de mon équipe (toutes casquettes confondues) : « Joueuse » ou « Joueur ». */
function selfGender(c: Ctx): TeamGender {
  const self = c.full.people.find(p => p.user_id === c.uid);
  const m = self ? c.full.memberships.find(x => x.person_id === self.id) : undefined;
  return (m && c.full.teams.find(t => t.id === m.team_id)?.gender) || 'X';
}

/** Vouvoiement d'un compte partenaire, même hors de son espace (D-074, D-103), tutoiement ailleurs. */
const tv = vousTv;
/** L'intro d'un tuto dans les mots de mon rôle (les notifications d'un bénévole, d'un partenaire, D-103). */
const introOf = (a: HelpArticle, c: Ctx) => { const r = notifRole(c); return (r !== 'membre' && a.introFor?.[r]) || a.intro; };

/** Les tutos de ce compte (un mineur : les siens, au texte qui lui parle, D-103), ceux des modules activés (D-122). */
const pool = (c: Ctx) => forModules(forAccount(ARTICLES, isMinorAccount(c), vouvoie(c)), k => modOn(c, k), siteOn(c), mvOpen(c));
/** Les tutos de mes casquettes (« Changer de casquette » : seulement avec plusieurs). */
const myArticles = (c: Ctx, roles: Set<HelpRole>) => forRoles(pool(c), roles).filter(a => !a.multiHat || c.hats.length > 1);

const iconOf = (a: HelpArticle) => THEMES.find(t => t.key === a.theme)?.icon ?? 'ti-doc';

/** « Y aller » : la page du tuto, si elle a un sens pour moi (`:equipe` = la première équipe que j'encadre). */
function routeOf(a: HelpArticle, c: Ctx, roles: Set<HelpRole>): string | null {
  if (!a.route) return null;
  const allowed = a.routeRoles ?? a.roles;
  if (!allowed.some(r => r === 'tous' || roles.has(r))) return null;
  if (!a.route.includes(':equipe')) return a.route;
  const team = c.full.teams.find(t => c.full.coachOf.has(t.id)) ?? c.full.teams.find(t => c.full.staffOf.has(t.id));
  return team ? a.route.replace(':equipe', team.id) : null;
}

const stepHtml = (s: string): Html => html`${stepParts(s).map(p => (p.ui ? html`<strong class="hp-ui">${p.text}</strong>` : p.text))}`;

export async function renderHelp(root: HTMLElement, ctx: Ctx, sub?: string): Promise<void> {
  if (sub === 'ecrire') return renderWrite(root, ctx);
  if (sub === 'messages') return renderMessages(root, ctx);
  if (sub === 'premiers-pas') return renderFirstStepsPage(root, ctx);
  if (sub === 'donnees') return renderDataPage(root, ctx);
  const article = sub ? pool(ctx).find(a => a.slug === sub) : undefined;
  if (article) return renderArticle(root, ctx, article);
  return renderHome(root, ctx);
}

// --- L'aide : tous les tutos de mes casquettes ----------------------------------------------
async function renderHome(root: HTMLElement, ctx: Ctx): Promise<void> {
  const roles = helpRoles(ctx);
  const mine = myArticles(ctx, roles);
  const every = pool(ctx);
  let all = remembered() && mine.length < every.length;
  let query = '';
  // D-122 : « Bénévole » seulement avec le bénévolat activé (ses tutos sont ceux du module ; sans lui, la casquette n'existe pas).
  const roleNames = [...roles].filter(r => r !== 'tous' && (r !== 'benevole' || modOn(ctx, 'benevolat'))).map(r => (r === 'joueur' ? agree(selfGender(ctx), 'Joueur', 'Joueuse') : ROLE_LABEL[r]));
  const chips = (CHIPS[personaOf(ctx)] ?? CHIPS.membre!).filter(([, slug]) => mine.some(a => a.slug === slug));
  mount(root, html`
    <section class="screen hp">
      <a class="back" href="#/moi" data-back>‹ Retour</a>
      <header class="screen-head"><p class="eyebrow">Aide</p><h2>Comment faire ?</h2></header>
      <label class="hp-search"><span class="ti ti-search" aria-hidden="true"></span>
        <input type="search" class="search" id="hp-q" placeholder="Chercher : absence, renfort, licence…" aria-label="Chercher dans l’aide" autocomplete="off" enterkeyhint="search"></label>
      <div id="hp-path"></div>
      ${chips.length ? html`<p class="eyebrow eyebrow-accent hp-want">${tv(ctx, 'Que veux-tu faire ?', 'Que voulez-vous faire ?')}</p>
        <div class="gd-chips">${chips.map(([label, slug]) => html`<a class="gd-chip" href="#/aide/${slug}">${label}</a>`)}</div>` : ''}
      <div class="hp-filter">
        <p class="muted small">${roleNames.length ? html`Tutos pour : <strong>${roleNames.join(' · ')}</strong>` : 'Les tutos de tous les rôles'}</p>
        ${mine.length < every.length ? html`<button type="button" class="chip-toggle" id="hp-all" aria-pressed="${String(all)}">Tout voir</button>` : ''}
      </div>
      <div id="hp-list" class="screen"></div>
      <section class="block hp-support">
        <h3>Une question, un bug, une idée ?</h3>
        <p class="muted">L’équipe qui fait Arceau lit chaque message et répond dans l’app.</p>
        <div class="btn-row">
          <a class="btn btn-primary" href="#/aide/ecrire"><span class="ti ti-help" aria-hidden="true"></span>Écrire au support</a>
          <a class="btn btn-ghost" href="#/aide/messages">Mes messages</a>
        </div>
      </section>
      <section class="block hp-data">
        <h3>${tv(ctx, 'Tes données', 'Vos données')}</h3>
        <ul class="list hp-list">
          <li><a href="#/aide/donnees"><span class="hp-ico" aria-hidden="true"><span class="ti ti-shield"></span></span><span class="card-main"><span class="hp-t">Mentions légales et données du club</span></span></a></li>
          <li><a href="${PRIVACY_URL}" target="_blank" rel="noopener"><span class="hp-ico" aria-hidden="true"><span class="ti ti-doc"></span></span><span class="card-main"><span class="hp-t">Politique de confidentialité de l’app</span></span></a></li>
        </ul>
      </section>
      <div id="hp-admin"></div>
    </section>`);

  const list = $(root, '#hp-list')!;
  // Une ligne : le tuto (sa page), la durée de son animation, « Montre-moi » s'il a une visite pour moi.
  const item = (a: HelpArticle) => {
    const other = !a.roles.some(r => r === 'tous' || roles.has(r));
    const show = !other && TOURS[a.slug] && (!a.route || routeOf(a, ctx, roles));
    return html`<li class="gd-row hp-row"><a class="t" href="#/aide/${a.slug}">
      <strong>${a.title}</strong><small class="hp-sub">${introOf(a, ctx)}</small>
      ${other ? html`<span class="hp-for">${a.roles.map(r => ROLE_LABEL[r]).join(' · ')}</span>` : ''}</a>
      ${FILMS[a.slug] ? html`<span class="gd-anim" title="Animation filmée sur la démo">▶ ${FILMS[a.slug]} s</span>` : ''}
      ${show ? html`<button type="button" class="btn btn-ghost btn-sm gd-show" data-tour="${a.slug}">Montre-moi</button>` : ''}</li>`;
  };
  const draw = () => {
    const base = all ? every : mine;
    if (!query.trim()) {
      mount(list, html`${byTheme(base).map(g => html`
        <section class="sec"><h3 class="sec-title">${g.label}</h3><ul class="gd-list hp-list">${g.items.map(item)}</ul></section>`)}`);
      return;
    }
    const found = searchArticles(base, query);
    const elsewhere = all ? [] : searchArticles(every, query).filter(a => !base.includes(a));
    mount(list, html`
      ${found.length ? html`<p class="eyebrow">${found.length} tuto${found.length > 1 ? 's' : ''}</p><ul class="gd-list hp-list">${found.map(item)}</ul>`
        : html`<div class="empty"><p class="empty-title">Aucun tuto ne parle de « ${query.trim()} »${elsewhere.length ? tv(ctx, ' pour tes casquettes', ' pour vos rôles') : ''}.</p>
            ${elsewhere.length ? '' : html`<p>${tv(ctx, 'Essaie un autre mot, ou écris-nous', 'Essayez un autre mot, ou écrivez-nous')} : l’aide se complète avec vos questions.</p>`}</div>`}
      ${elsewhere.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="hp-more">Voir ${elsewhere.length} tuto${elsewhere.length > 1 ? 's' : ''} des autres rôles</button>` : ''}`);
    $(list, '#hp-more')?.addEventListener('click', () => setAll(true));
  };
  const setAll = (on: boolean) => {
    all = on; remember(on);
    const b = $(root, '#hp-all');
    if (b) b.setAttribute('aria-pressed', String(on));
    draw();
  };
  $(root, '#hp-all')?.addEventListener('click', () => setAll(!all));
  const q = $<HTMLInputElement>(root, '#hp-q')!;
  q.addEventListener('input', () => { query = q.value; draw(); });
  draw();
  void renderSupportRow($(root, '#hp-admin')!, ctx);
  void renderPathCard($(root, '#hp-path')!, ctx);
}

/** « Que veux-tu faire ? » : les gestes les plus demandés, par rôle (un tuto chacun). */
const CHIPS: Partial<Record<Persona, [string, string][]>> = {
  bureau: [['Relancer les dossiers', 'dossiers-licence'], ['Trouver des bénévoles', 'demander-benevoles'], ['Inviter un coach', 'membres-roles'],
    ['Demander une dérogation', 'derogation'], ['Le devis Arceau', 'abonnement']],
  coach: [['Faire l’appel', 'faire-appel'], ['Programmer', 'programmer'], ['Convoquer un renfort', 'convoquer-match'], ['Créer un play', 'creer-play'],
    ['Inviter l’équipe', 'inviter-equipe']],
  joueur: [['Prévenir d’une absence', 'prevenir-absence'], ['Activer les notifications', 'notifications'], ['Voir un play', 'voir-play'], ['Prendre un créneau', 'prendre-creneau']],
  parent: [['Prévenir d’une absence', 'prevenir-absence'], ['Le code perso', 'code-perso'], ['Prendre un créneau', 'prendre-creneau'], ['Activer les notifications', 'notifications']],
  partenaire: [['Répondre à un appel', 'partenaire-appel'], ['Demander de la visibilité', 'partenaire-visibilite']],
  supporter: [['Prendre un créneau', 'prendre-creneau'], ['Encourager une équipe', 'encourager'], ['Activer les notifications', 'notifications']],
  membre: [['Activer les notifications', 'notifications'], ['Se connecter', 'se-connecter']],
  abonne: [['Suivre une équipe', 'suivre'], ['Activer les notifications', 'notifications']],
};

// --- Mentions légales et données du club (D-103) ----------------------------------------------------
async function renderDataPage(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club && !ctx.club.id.startsWith('host:') ? ctx.club : null;
  const info = club ? await clubLegal({ club: club.id }).catch(() => null) : null;
  // La même page, sans compte : le sous-domaine du club, sinon son adresse publique.
  const pub = info ? (hostClubSlug() ? '/donnees' : info.slug ? `/c/${info.slug}/donnees` : null) : null;
  mount(root, html`
    <section class="screen hp lg-in">
      <a class="back" href="#/aide" data-back>‹ Aide</a>
      <header class="screen-head"><p class="eyebrow">${info?.name ?? 'Aide'}</p><h2>Mentions légales et données</h2></header>
      ${info ? clubDataBody(info, 'h3') : html`<div class="empty"><p class="empty-title">Pas de club affiché.</p>
        <p><a href="${PRIVACY_URL}" target="_blank" rel="noopener">Politique de confidentialité de l’app</a></p></div>`}
      ${pub ? html`<p class="hint">À partager : <a href="${pub}" target="_blank" rel="noopener">la page publique</a>, sans compte.</p>` : ''}
    </section>`);
}

// --- Mes premiers pas (D-091) ----------------------------------------------------------------------
async function renderFirstStepsPage(root: HTMLElement, ctx: Ctx): Promise<void> {
  mount(root, html`
    <section class="screen hp">
      <a class="back" href="#/aide" data-back>‹ Aide</a>
      <header class="screen-head"><p class="eyebrow">Aide</p><h2>${tv(ctx, 'Mes premiers pas', 'Vos premiers pas')}</h2></header>
      <p class="muted">${tv(ctx, 'Chaque étape se coche toute seule quand elle est faite. « Montre-moi » te guide geste par geste.',
        'Chaque étape se coche toute seule quand elle est faite. « Montre-moi » vous guide geste par geste.')}</p>
      <div id="fs-all" class="screen"></div>
    </section>`);
  await renderAllFirstSteps($(root, '#fs-all')!, ctx);
}

/** Pour l'équipe Arceau (admins de la plateforme) : la boîte du support. Rien pour les autres. */
export async function renderSupportRow(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!(await isPlatformAdmin(ctx.uid))) return;
  mount(el, html`<a class="card card-row" href="#/support">
    <span class="play-thumb" aria-hidden="true"><span class="ti ti-inbox"></span></span>
    <span class="card-main"><span class="card-title">Boîte du support</span><span class="muted small">Équipe Arceau : les messages de tous les clubs.</span></span>
    <span class="occ-go" aria-hidden="true">›</span></a>`);
}

// --- Un tuto -------------------------------------------------------------------------------------
function renderArticle(root: HTMLElement, ctx: Ctx, a: HelpArticle): void {
  const roles = helpRoles(ctx);
  const go = routeOf(a, ctx, roles);
  const theme = THEMES.find(t => t.key === a.theme);
  const related = myArticles(ctx, roles).filter(x => x.theme === a.theme && x !== a).slice(0, 3);
  const tour = TOURS[a.slug] && (!a.route || go) ? a.slug : null;
  const film = FILMS[a.slug];
  mount(root, html`
    <article class="screen hp">
      <a class="back" href="#/aide" data-back>‹ Aide</a>
      <header class="screen-head"><p class="eyebrow">${theme?.label ?? 'Aide'}</p><h2>${a.title}</h2></header>
      ${tour ? html`<section class="block gd-demo" data-guide="tuto-demo">
        <div class="gd-demo-hero ${film ? '' : 'gd-nofilm'}">
          ${film ? html`<button type="button" class="gd-phone" id="gd-film" aria-label="Voir l’animation">
            <img src="/guide/${a.slug}.jpg" alt="" loading="lazy" data-anim="/guide/${a.slug}.webp"><span class="play" aria-hidden="true"><b>▶</b></span></button>` : ''}
          <div class="gd-demo-txt">
            <p class="eyebrow eyebrow-accent">${film ? `En animation · ${film} s` : 'Dans l’app'}</p>
            <strong>${tv(ctx, 'Regarde le geste, puis fais-le', 'Regardez le geste, puis faites-le')}</strong>
            <span class="muted small">${film ? 'Filmé sur le club de démo, mis à jour à chaque nouvelle version de l’app.'
              : tv(ctx, 'Le guide t’emmène sur l’écran et éclaire chaque geste.', 'Le guide vous emmène sur l’écran et éclaire chaque geste.')}</span>
            <button type="button" class="btn btn-primary btn-sm gd-show" data-tour="${a.slug}">Montre-moi dans l’app</button>
          </div></div></section>` : ''}
      <p class="lead">${introOf(a, ctx)}</p>
      <ol class="hp-steps">${a.steps.map((s, i) => html`<li><span class="hp-n" aria-hidden="true">${i + 1}</span><span>${stepHtml(s)}</span></li>`)}</ol>
      ${go ? html`<a class="btn btn-primary hp-go" href="${go}">Y aller</a>` : ''}
      ${a.tips?.length ? html`<section class="block hp-tips"><h3>Bon à savoir</h3><ul>${a.tips.map(t => html`<li>${stepHtml(t)}</li>`)}</ul></section>` : ''}
      ${related.length ? html`<section class="sec"><h3 class="sec-title">Voir aussi</h3><ul class="list hp-list">${related.map(r => html`
        <li><a href="#/aide/${r.slug}"><span class="hp-ico" aria-hidden="true"><span class="ti ${iconOf(r)}"></span></span><span class="card-main"><span class="hp-t">${r.title}</span></span></a></li>`)}</ul></section>` : ''}
      <p class="muted small hp-more">Pas la réponse ? <a href="#/aide/ecrire">Écrire au support</a></p>
      <div class="gd-fb" id="gd-fb">
        <span>${tv(ctx, 'Ce tuto t’a aidé ?', 'Ce tuto vous a aidé ?')}</span>
        <span class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-fb="yes"><span aria-hidden="true">👍</span> Oui</button>
          <button type="button" class="btn btn-ghost btn-sm" data-fb="no">Pas vraiment</button></span>
      </div>
    </article>`);
  // L'animation : l'affiche d'abord, la boucle au toucher (rien ne bouge tout seul).
  $(root, '#gd-film')?.addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLElement, img = b.querySelector('img')!;
    if (b.classList.toggle('on')) img.src = img.dataset.anim!; else img.src = `/guide/${a.slug}.jpg`;
  });
  wireFeedback($(root, '#gd-fb')!, ctx, a);
}

/** « Ce tuto t'a aidé ? » : « Oui » s'enregistre ; « Pas vraiment » ouvre une ligne qui part au support. */
function wireFeedback(el: HTMLElement, ctx: Ctx, a: HelpArticle): void {
  const club = ctx.club?.id && !ctx.club.id.startsWith('host:') ? ctx.club.id : null;
  el.querySelector('[data-fb="yes"]')?.addEventListener('click', () => {
    void guideFeedback(a.slug, true, club).catch(() => undefined);
    mount(el, html`<span class="gd-fb-done">Merci ! ${tv(ctx, 'Ta réponse aide à améliorer les tutos.', 'Votre réponse aide à améliorer les tutos.')}</span>`);
  });
  el.querySelector('[data-fb="no"]')?.addEventListener('click', () => {
    void guideFeedback(a.slug, false, club).catch(() => undefined);
    mount(el, html`
      <form class="gd-fb-form" novalidate>
        <label class="field"><span>${tv(ctx, 'Qu’est-ce qui manquait ?', 'Qu’est-ce qui manquait ?')}</span>
          <input name="body" maxlength="500" required placeholder="${tv(ctx, 'ex. je ne trouve pas le bouton', 'ex. je ne trouve pas le bouton')}" enterkeyhint="send"></label>
        <button class="btn btn-primary btn-sm" type="submit">Envoyer au support</button>
        <p class="form-msg" role="alert"></p>
      </form>`);
    const f = el.querySelector<HTMLFormElement>('form')!;
    f.querySelector('input')?.focus();
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const body = String(new FormData(f).get('body') ?? '').trim();
      const msg = f.querySelector<HTMLElement>('.form-msg')!;
      if (!body) { msg.textContent = tv(ctx, 'Écris quelques mots.', 'Écrivez quelques mots.'); return; }
      const btn = f.querySelector<HTMLButtonElement>('button')!;
      btn.disabled = true;
      try {
        await sendSupport({ club_id: club, kind: 'question', body: `Tuto « ${a.title} » : ${body}`, page: `tuto:${a.slug}`,
          app_version: await appVersion(), user_agent: navigator.userAgent.slice(0, 400), casquette: casquetteOf(ctx).slice(0, 80), screenshot: null });
        mount(el, html`<span class="gd-fb-done">${tv(ctx, 'Merci ! L’équipe Arceau te répond dans « Mes messages ».', 'Merci ! L’équipe Arceau vous répond dans « Mes messages ».')}</span>`);
      } catch (err) {
        btn.disabled = false;
        msg.textContent = (err as { code?: string }).code === '54000' ? tv(ctx, 'Trop de messages aujourd’hui : réessaie demain.', 'Trop de messages aujourd’hui : réessayez demain.') : 'Envoi impossible pour le moment.';
      }
    });
  });
}

// --- Écrire au support ---------------------------------------------------------------------------

/** La casquette affichée, en mots. */
export function casquetteOf(c: Ctx): string {
  const hat = c.hats.find(h => h.key === c.hat);
  if (hat) return hat.label;
  const self = me(c);
  const coached = c.teams.filter(t => c.coachOf.has(t.id)).map(t => t.name);
  const played = self ? c.teams.filter(t => c.memberships.some(m => m.person_id === self.id && m.team_id === t.id)).map(t => t.name) : [];
  switch (personaOf(c)) {
    case 'coach': return c.roles.some(r => r.role === 'technical') ? 'Resp. technique' : ['Coach', ...coached.slice(0, 2)].join(' · ');
    case 'bureau': return 'Bureau';
    case 'joueur': return [agree(selfGender(c), 'Joueur', 'Joueuse'), ...played.slice(0, 2)].join(' · ');
    case 'parent': return 'Parent';
    case 'partenaire': return `Partenaire${c.partners[0] ? ` · ${c.partners[0].name}` : ''}`;
    case 'supporter': return 'Bénévole';
    default: return 'Membre';
  }
}

/** Capture réduite : JPEG, 1 280 px au plus, sous la limite de la base (700 Ko). */
export async function compressShot(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  try {
    for (const [max, quality] of [[1280, 0.82], [1280, 0.68], [1280, 0.55], [1024, 0.55], [800, 0.5]] as const) {
      const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
      const cv = document.createElement('canvas');
      cv.width = Math.max(1, Math.round(bmp.width * k)); cv.height = Math.max(1, Math.round(bmp.height * k));
      const g = cv.getContext('2d')!;
      g.fillStyle = '#fff'; g.fillRect(0, 0, cv.width, cv.height);   // une capture transparente ne devient pas noire
      g.drawImage(bmp, 0, 0, cv.width, cv.height);
      const url = cv.toDataURL('image/jpeg', quality);
      if (url.startsWith('data:image/jpeg;base64,') && url.length <= SHOT_MAX) return url;
    }
  } finally { bmp.close(); }
  throw new Error('trop lourde');
}

const ko = (n: number) => `${Math.max(1, Math.round(n * 0.75 / 1024)).toLocaleString('fr-FR')} Ko`;

async function renderWrite(root: HTMLElement, ctx: Ctx): Promise<void> {
  const version = await appVersion();
  const page = pageOf(fromPage) || '#/';
  const ua = navigator.userAgent.slice(0, 400);
  const casquette = casquetteOf(ctx).slice(0, 80);
  let kind: SupportKind = 'bug';
  let shot: string | null = null;
  mount(root, html`
    <section class="screen hp">
      <a class="back" href="#/aide" data-back>‹ Aide</a>
      <header class="screen-head"><p class="eyebrow">Support Arceau</p><h2>Écrire au support</h2></header>
      <p class="muted">L’équipe qui fait Arceau (pas le club) lit chaque message. La réponse arrive dans « Mes messages », avec une notification.</p>
      <form class="form" id="f-sup" novalidate>
        <div class="seg" role="group" aria-label="Sorte de message" data-guide="sp-kind">${(Object.keys(KIND_LABEL) as SupportKind[]).map(k => html`
          <button type="button" data-kind="${k}" aria-pressed="${String(k === kind)}">${KIND_LABEL[k]}</button>`)}</div>
        <p class="hint" id="sp-hint">${KIND_HINT[kind]}</p>
        <label class="field"><span>Message</span>
          <textarea name="body" id="sp-body" data-guide="sp-body" rows="7" maxlength="${BODY_MAX}" required placeholder="Décrire ici…"></textarea>
          <small class="hp-count" id="sp-count">0 / ${BODY_MAX.toLocaleString('fr-FR')}</small></label>
        <div class="field"><span>Capture d’écran (facultatif)</span>
          <div id="sp-shot"></div></div>
        <section class="hp-ctx" aria-label="Joint au message">
          <p class="eyebrow">Joint au message</p>
          <dl>
            <dt>Page</dt><dd>${pageName(page)} <span class="muted mono">${page}</span></dd>
            <dt>Version</dt><dd>${version ?? 'développement (pas de version publiée)'}</dd>
            <dt>Navigateur</dt><dd>${browserLabel(ua)}</dd>
            <dt>Club</dt><dd>${ctx.club?.name ?? '—'}</dd>
            <dt>Casquette</dt><dd>${casquette}</dd>
          </dl>
        </section>
        <p class="form-msg" id="sp-msg"></p>
        <button class="btn btn-primary" type="submit" id="sp-send" data-guide="sp-send">Envoyer</button>
        <p class="hint">${DAILY_MAX} messages par jour au plus.</p>
      </form>
    </section>`);

  const hint = $(root, '#sp-hint')!;
  for (const b of $$<HTMLButtonElement>(root, '[data-kind]')) {
    b.addEventListener('click', () => {
      kind = b.dataset.kind as SupportKind;
      $$(root, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      hint.textContent = KIND_HINT[kind];
    });
  }
  const body = $<HTMLTextAreaElement>(root, '#sp-body')!;
  const count = $(root, '#sp-count')!;
  body.addEventListener('input', () => { count.textContent = `${body.value.length.toLocaleString('fr-FR')} / ${BODY_MAX.toLocaleString('fr-FR')}`; });

  const shotEl = $(root, '#sp-shot')!;
  const drawShot = () => {
    mount(shotEl, shot ? html`
      <div class="hp-shot"><img src="${shot}" alt="Capture jointe"><span class="muted small">${ko(shot.length)}</span>
        <button type="button" class="btn btn-link btn-sm" id="sp-shot-x">Retirer</button></div>`
      : html`<label class="btn btn-ghost btn-sm hp-file">Ajouter une capture<input type="file" id="sp-file" accept="image/*" hidden></label>`);
    $(shotEl, '#sp-shot-x')?.addEventListener('click', () => { shot = null; drawShot(); });
    $<HTMLInputElement>(shotEl, '#sp-file')?.addEventListener('change', async (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (!f) return;
      if (!f.type.startsWith('image/')) { toast('Une image seulement (capture d’écran, photo).', 'ko'); return; }
      try { shot = await compressShot(f); drawShot(); }
      catch { toast('Image illisible ou trop lourde.', 'ko'); }
    });
  };
  drawShot();

  const form = $<HTMLFormElement>(root, '#f-sup')!;
  const msg = $(root, '#sp-msg')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = body.value.trim();
    if (!text) { msg.textContent = tv(ctx, 'Écris ton message.', 'Écrivez votre message.'); body.focus(); return; }
    msg.textContent = '';
    const send = $<HTMLButtonElement>(root, '#sp-send')!;
    send.disabled = true;
    try {
      await sendSupport({ club_id: ctx.club?.id && !ctx.club.id.startsWith('host:') ? ctx.club.id : null, kind, body: text, page, app_version: version,
        user_agent: ua, casquette, screenshot: shot });
    } catch (err) {
      send.disabled = false;
      const code = (err as { code?: string }).code;
      msg.textContent = code === '54000' ? tv(ctx, `${DAILY_MAX} messages par jour au plus : réessaie demain.`, `${DAILY_MAX} messages par jour au plus : réessayez demain.`)
        : 'Envoi impossible pour le moment.';
      return;
    }
    mount(root, html`
      <section class="screen hp">
        <div class="empty hp-done">
          <span class="hp-done-ico" aria-hidden="true"><span class="ti ti-check"></span></span>
          <p class="empty-title">Message envoyé</p>
          <p>Merci ! La réponse arrivera dans « Mes messages », avec une notification.</p>
          <div class="btn-row"><a class="btn btn-primary" href="#/aide/messages">Mes messages</a><a class="btn btn-ghost" href="${page}">Revenir où j’étais</a></div>
        </div>
      </section>`);
  });
}

// --- Mes messages --------------------------------------------------------------------------------
const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).replace(':', 'h');

async function renderMessages(root: HTMLElement, ctx: Ctx): Promise<void> {
  const list = await mySupportMessages(ctx.uid).catch(() => null);
  const card = (m: SupportMessage) => html`
    <article class="card hp-msg">
      <div class="hp-msg-top"><span class="chip chip-soft">${messageLabel(m, personaOf(ctx) === 'partenaire')}</span><span class="chip ${STATUS_CHIP[m.status]}">${STATUS_LABEL[m.status]}</span>
        <span class="muted small">${when(m.created_at)}</span></div>
      ${m.topic && m.page_label ? html`<p class="muted small">Sur : ${m.page_label}</p>` : ''}
      <p class="note">${m.body}</p>
      ${m.reply ? html`<div class="hp-reply"><p class="eyebrow eyebrow-accent">Réponse de l’équipe Arceau${m.replied_at ? ` · ${when(m.replied_at)}` : ''}</p><p class="note">${m.reply}</p></div>`
        : html`<p class="muted small">${m.status === 'new' ? 'Pas encore lu.' : m.status === 'done' || m.status === 'wontfix' ? 'Traité, sans réponse écrite.' : 'L’équipe s’en occupe.'}</p>`}
    </article>`;
  mount(root, html`
    <section class="screen hp">
      <a class="back" href="#/aide" data-back>‹ Aide</a>
      <header class="screen-head head-row"><div><p class="eyebrow">Support Arceau</p><h2>Mes messages</h2></div>
        <a class="btn btn-primary btn-sm" href="#/aide/ecrire">Écrire</a></header>
      ${list === null ? html`<div class="empty"><p class="empty-title">Chargement impossible.</p></div>`
        : list.length ? list.map(card)
        : html`<div class="empty"><p class="empty-title">Aucun message pour l’instant.</p><p>Un bug, une idée, une question : l’équipe Arceau répond ici.</p>
            <a class="btn btn-primary" href="#/aide/ecrire">Écrire au support</a></div>`}
    </section>`);
}

/** Lien discret « Aide et support » en bas des accueils. */
export const helpFoot = (): Html => html`<a class="hp-foot" href="#/aide"><span class="ti ti-help" aria-hidden="true"></span>Aide et support</a>`;
